import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { controller } from '../../api/controller';
import { usePublicUiLocale } from '../../contexts/PublicUiLocaleContext';
import HelpHint from '../../components/HelpHint';
import { uidNameConflictMessage, isUidNameConflictError } from '../../project-modules/uidNameConflictHelpers';

function parseApiMessage(err, fallback) {
  const msg = err?.response?.data?.message;
  if (typeof msg === 'string') return msg;
  if (typeof msg === 'object' && msg !== null && typeof msg.message === 'string') {
    return msg.message;
  }
  return fallback;
}

export default function RenameUidDialog({
  open,
  uid,
  onClose,
  onRenamed,
}) {
  const { sa } = usePublicUiLocale();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && uid) {
      setName(uid.name || '');
      setError('');
    }
  }, [open, uid]);

  const handleSubmit = async () => {
    if (!uid?.id) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError(sa.uidRenameRequired);
      return;
    }
    if (trimmed === String(uid.name || '').trim()) {
      onClose();
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await controller.updateUid(uid.id, { name: trimmed });
      onRenamed?.();
      onClose();
    } catch (e) {
      setError(
        isUidNameConflictError(e)
          ? uidNameConflictMessage(e, sa.uidNameConflict)
          : parseApiMessage(e, sa.createFailed),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {sa.renameUidTitle}
        <HelpHint title={sa.helpRenameUidTitle} text={sa.helpRenameUid} />
      </DialogTitle>
      <DialogContent>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {sa.renameUidCodeHint}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={sa.name}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>{sa.cancel}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? sa.creating : sa.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RenameUidDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  uid: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    code: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onRenamed: PropTypes.func,
};
